// Catalog Intelligence V1 — MAGI #2.
// Turns each eligible catalog title into a structured, searchable
// intelligence profile using Kandake's canonical taxonomy:
//   genre, mood, tempo, vocal_type, instrumentation,
//   best_use, themes, era_style, media_type
// V1 scope: analyze (deterministic, from existing Kandake track data),
// review, edit, save, search/filter. No BI integration, no matching,
// no ranking, NO subgenres. Rights/clearance data stays authoritative
// on the Track record and is shown read-only for context only.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Disc3, Loader2, Pencil, RefreshCw, Search, Sparkles, X,
} from "lucide-react";

import { api, errMsg } from "@/lib/api";
import { canAccessBriefIntelligence, canWrite, useAuth } from "@/lib/auth";
import EmptyState, { PageHeader } from "@/components/Common";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";

// Canonical taxonomy — exact Kandake-wide field names. Do not rename.
const SINGLE_FIELDS = [
  { key: "genre", label: "Genre" },
  { key: "tempo", label: "Tempo" },
  { key: "vocal_type", label: "Vocal Type" },
];
const MULTI_FIELDS = [
  { key: "mood", label: "Mood" },
  { key: "instrumentation", label: "Instrumentation" },
  { key: "best_use", label: "Best Use" },
  { key: "themes", label: "Themes" },
  { key: "era_style", label: "Era / Style" },
  { key: "media_type", label: "Media Type" },
];
const ALL_FIELDS = [...SINGLE_FIELDS, ...MULTI_FIELDS];

const EMPTY_FILTERS = ALL_FIELDS.reduce(
  (acc, f) => ({ ...acc, [f.key]: "" }),
  { q: "", ci_status: "" },
);

function emptyForm() {
  const form = {};
  for (const f of SINGLE_FIELDS) form[f.key] = "";
  for (const f of MULTI_FIELDS) form[f.key] = [];
  return form;
}

// Sentinel for "clear" inside Radix Select (empty string is reserved).
const NOT_SET = "__not_set__";

function profileToForm(profile) {
  const form = emptyForm();
  if (!profile) return form;
  for (const f of SINGLE_FIELDS) form[f.key] = profile[f.key] || "";
  for (const f of MULTI_FIELDS) {
    form[f.key] = Array.isArray(profile[f.key]) ? profile[f.key] : [];
  }
  return form;
}

function formatDate(value) {
  if (!value) return "\u2014";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "\u2014" : date.toLocaleString();
}

function statusBadgeVariant(status) {
  if (status === "Operator Approved") return "default";
  if (status === "Generated") return "secondary";
  return "outline";
}

export default function CatalogIntelligence() {
  const { user } = useAuth();
  const writer = canWrite(user);

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  // Kandake Common Taxonomy V1 approved values (served by the backend —
  // single source of truth for the nine canonical fields).
  const [vocab, setVocab] = useState({});

  // Editor sheet state.
  const [openTrackId, setOpenTrackId] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [reviewRequired, setReviewRequired] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(async (activeFilters) => {
    setLoading(true);
    setLoadError("");
    try {
      const params = {};
      for (const [key, value] of Object.entries(activeFilters)) {
        if (String(value || "").trim()) params[key] = String(value).trim();
      }
      params.limit = 100;
      const response = await api.get("/catalog-intelligence/tracks", { params });
      setRows(Array.isArray(response.data?.rows) ? response.data.rows : []);
    } catch (error) {
      setRows([]);
      setLoadError(errMsg(error, "Could not load Catalog Intelligence results."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(EMPTY_FILTERS); }, [load]);

  useEffect(() => {
    let active = true;
    api.get("/catalog-intelligence/vocabulary")
      .then((response) => {
        if (active) setVocab(response.data?.fields || {});
      })
      .catch(() => { if (active) setVocab({}); });
    return () => { active = false; };
  }, []);

  async function openTrack(trackId) {
    setOpenTrackId(trackId);
    setDetail(null);
    setReviewRequired([]);
    setDetailLoading(true);
    try {
      const response = await api.get(`/catalog-intelligence/tracks/${trackId}`);
      setDetail(response.data);
      setForm(profileToForm(response.data?.profile));
    } catch (error) {
      toast.error(errMsg(error, "Could not open the catalog title."));
      setOpenTrackId("");
    } finally {
      setDetailLoading(false);
    }
  }

  async function generateAnalysis() {
    if (!openTrackId) return;
    setBusy("analyze");
    try {
      const response = await api.post(
        `/catalog-intelligence/tracks/${openTrackId}/analyze`,
      );
      const data = response.data || {};
      setForm(profileToForm(data.draft));
      setReviewRequired(Array.isArray(data.review_required) ? data.review_required : []);
      if (data.profile) {
        setDetail((prev) => (prev ? { ...prev, profile: data.profile } : prev));
      }
      toast.success(
        data.persisted
          ? "Analysis generated and saved as a draft profile."
          : "Analysis generated. Review and save to apply.",
      );
    } catch (error) {
      // Fail-safe: existing profile/form data is untouched on failure.
      toast.error(errMsg(error, "Analysis could not run. Please retry."));
    } finally {
      setBusy("");
    }
  }

  async function saveProfile() {
    if (!openTrackId) return;
    setBusy("save");
    try {
      const body = {};
      for (const f of SINGLE_FIELDS) body[f.key] = form[f.key] || "";
      for (const f of MULTI_FIELDS) {
        body[f.key] = Array.isArray(form[f.key]) ? form[f.key] : [];
      }
      const response = await api.put(
        `/catalog-intelligence/tracks/${openTrackId}`,
        body,
      );
      const profile = response.data?.profile;
      setDetail((prev) => (prev ? { ...prev, profile } : prev));
      setForm(profileToForm(profile));
      setReviewRequired([]);
      toast.success("Intelligence profile saved.");
      load(filters);
    } catch (error) {
      toast.error(errMsg(error, "Could not save the intelligence profile."));
    } finally {
      setBusy("");
    }
  }

  if (!canAccessBriefIntelligence(user)) {
    return (
      <div className="p-6">
        <EmptyState
          icon={Disc3}
          title="Not authorised"
          description="Catalog Intelligence is available to authorised Kandake operators only."
        />
      </div>
    );
  }

  const track = detail?.track;
  const profile = detail?.profile;

  return (
    <div className="space-y-6" data-testid="catalog-intelligence-page">
      <PageHeader
        title="Catalog Intelligence"
        subtitle="Structured, searchable intelligence profiles for catalog titles using Kandake's canonical taxonomy. Rights and clearance remain governed by existing catalog controls."
      />

      {/* Filters — the nine canonical fields plus free-text search. */}
      <Card data-testid="ci-filters-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Search &amp; Filter</CardTitle>
          <CardDescription>
            Filter the catalog by canonical intelligence fields.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="space-y-1 lg:col-span-2">
              <Label className="text-xs" htmlFor="ci-filter-q">Title / Artist</Label>
              <Input
                id="ci-filter-q"
                value={filters.q}
                onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))}
                placeholder="Search title, artist, catalog ID"
                data-testid="ci-filter-q"
              />
            </div>
            {ALL_FIELDS.map((f) => (
              <div className="space-y-1" key={f.key}>
                <Label className="text-xs" htmlFor={`ci-filter-${f.key}`}>{f.label}</Label>
                <Input
                  id={`ci-filter-${f.key}`}
                  value={filters[f.key]}
                  onChange={(e) =>
                    setFilters((p) => ({ ...p, [f.key]: e.target.value }))
                  }
                  placeholder={f.label}
                  data-testid={`ci-filter-${f.key}`}
                />
              </div>
            ))}
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="ci-filter-status">CI Status</Label>
              <select
                id="ci-filter-status"
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                value={filters.ci_status}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, ci_status: e.target.value }))
                }
                data-testid="ci-filter-status"
              >
                <option value="">All statuses</option>
                <option value="Not Analyzed">Not Analyzed</option>
                <option value="Generated">Generated</option>
                <option value="Operator Approved">Operator Approved</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => load(filters)}
              disabled={loading}
              data-testid="ci-apply-filters-btn"
            >
              {loading
                ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                : <Search className="w-4 h-4 mr-1" />}
              Apply
            </Button>
            <Button
              variant="outline"
              onClick={() => { setFilters(EMPTY_FILTERS); load(EMPTY_FILTERS); }}
              disabled={loading}
              data-testid="ci-clear-filters-btn"
            >
              <RefreshCw className="w-4 h-4 mr-1" /> Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card data-testid="ci-results-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            Catalog Titles {loading ? "" : `(${rows.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadError && (
            <Alert variant="destructive" className="mb-3" data-testid="ci-load-error">
              <AlertDescription>{loadError}</AlertDescription>
            </Alert>
          )}
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading catalog…
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={Disc3}
              title="No catalog titles match"
              description="Adjust the filters or clear them to view the full catalog."
            />
          ) : (
            <div className="divide-y divide-border rounded-md border border-border">
              {rows.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => openTrack(row.id)}
                  className="w-full text-left px-4 py-3 hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none transition-colors"
                  data-testid={`ci-row-${row.id}`}
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">
                        {row.track_title || "Untitled"}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {row.artist || "\u2014"}
                        {row.track_id ? ` · ${row.track_id}` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {row.profile?.genre && (
                        <Badge variant="outline" className="text-[10px]">
                          {row.profile.genre}
                        </Badge>
                      )}
                      {row.profile?.tempo && (
                        <Badge variant="outline" className="text-[10px]">
                          {row.profile.tempo}
                        </Badge>
                      )}
                      <Badge
                        variant={statusBadgeVariant(row.ci_status)}
                        className="text-[10px]"
                        data-testid={`ci-row-status-${row.id}`}
                      >
                        {row.ci_status}
                      </Badge>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Profile editor sheet */}
      <Sheet
        open={!!openTrackId}
        onOpenChange={(open) => { if (!open) setOpenTrackId(""); }}
      >
        <SheetContent
          side="right"
          className="w-full sm:max-w-xl overflow-y-auto"
          data-testid="ci-editor-sheet"
        >
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Disc3 className="w-4 h-4" />
              {track?.track_title || "Catalog title"}
            </SheetTitle>
            <SheetDescription>
              Intelligence profile — canonical Kandake taxonomy. Rights and
              clearance are managed on the Track record, not here.
            </SheetDescription>
          </SheetHeader>

          {detailLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-10 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : track ? (
            <div className="space-y-5 py-4">
              {/* Read-only track context (authoritative data untouched). */}
              <div className="rounded-md border border-border p-3 space-y-1 text-xs" data-testid="ci-track-summary">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">Artist</span>
                  <span>{track.artist || "\u2014"}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">Catalog ID</span>
                  <span>{track.track_id || "\u2014"}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">Clearance (read-only)</span>
                  <Badge variant="outline" className="text-[10px]" data-testid="ci-clearance-badge">
                    {track.clearance_status || "\u2014"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">CI Status</span>
                  <Badge
                    variant={statusBadgeVariant(profile?.ci_status || "Not Analyzed")}
                    className="text-[10px]"
                    data-testid="ci-profile-status"
                  >
                    {profile?.ci_status || "Not Analyzed"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">Last updated</span>
                  <span data-testid="ci-profile-updated">{formatDate(profile?.updated_at)}</span>
                </div>
              </div>

              {writer && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={generateAnalysis}
                    disabled={!!busy}
                    data-testid="ci-generate-btn"
                  >
                    {busy === "analyze"
                      ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      : <Sparkles className="w-4 h-4 mr-1" />}
                    Generate Analysis
                  </Button>
                  <Button
                    size="sm"
                    onClick={saveProfile}
                    disabled={!!busy}
                    data-testid="ci-save-btn"
                  >
                    {busy === "save"
                      ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      : <Pencil className="w-4 h-4 mr-1" />}
                    Save Profile
                  </Button>
                </div>
              )}

              {reviewRequired.length > 0 && (
                <Alert data-testid="ci-review-required">
                  <AlertDescription className="text-xs">
                    Operator review required — analysis had insufficient
                    source information for:{" "}
                    {reviewRequired.join(", ")}.
                  </AlertDescription>
                </Alert>
              )}

              {/* Canonical fields — Kandake Common Taxonomy V1 values only. */}
              <div className="space-y-3">
                {SINGLE_FIELDS.map((f) => (
                  <div className="space-y-1" key={f.key}>
                    <Label className="text-xs">{f.label}</Label>
                    <Select
                      value={form[f.key] || NOT_SET}
                      onValueChange={(value) =>
                        setForm((p) => ({
                          ...p,
                          [f.key]: value === NOT_SET ? "" : value,
                        }))
                      }
                      disabled={!writer}
                    >
                      <SelectTrigger
                        className="h-9"
                        data-testid={`ci-field-${f.key}`}
                      >
                        <SelectValue placeholder="Not set" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NOT_SET}>— Not set —</SelectItem>
                        {(vocab[f.key] || []).map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
                {MULTI_FIELDS.map((f) => (
                  <div className="space-y-1" key={f.key}>
                    <Label className="text-xs">{f.label}</Label>
                    {writer && (
                      <Select
                        value=""
                        onValueChange={(value) =>
                          setForm((p) => ({
                            ...p,
                            [f.key]: p[f.key].includes(value)
                              ? p[f.key]
                              : [...p[f.key], value],
                          }))
                        }
                      >
                        <SelectTrigger
                          className="h-9"
                          data-testid={`ci-field-${f.key}`}
                        >
                          <SelectValue placeholder={`Add ${f.label.toLowerCase()}…`} />
                        </SelectTrigger>
                        <SelectContent>
                          {(vocab[f.key] || [])
                            .filter((option) => !form[f.key].includes(option))
                            .map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    )}
                    {form[f.key].length > 0 ? (
                      <div className="flex flex-wrap gap-1 pt-0.5">
                        {form[f.key].map((item) => (
                          <Badge
                            key={item}
                            variant="secondary"
                            className="text-[10px] gap-1"
                            data-testid={`ci-chip-${f.key}-${item}`}
                          >
                            {item}
                            {writer && (
                              <button
                                type="button"
                                aria-label={`Remove ${item}`}
                                className="ml-0.5 hover:text-destructive"
                                onClick={() =>
                                  setForm((p) => ({
                                    ...p,
                                    [f.key]: p[f.key].filter((v) => v !== item),
                                  }))
                                }
                                data-testid={`ci-chip-remove-${f.key}-${item}`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      !writer && (
                        <p className="text-[11px] text-muted-foreground">Not set</p>
                      )
                    )}
                  </div>
                ))}
              </div>

              {!writer && (
                <p className="text-xs text-muted-foreground">
                  Read-only access — profile edits require a catalog writer role.
                </p>
              )}
            </div>
          ) : (
            <div className="py-10">
              <EmptyState
                icon={X}
                title="Could not load title"
                description="Close and retry."
              />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
